import { ApplicationController } from "../../support/application-controller";

/**
 * Stimulus controller for live search on the admin subscriptions page.
 * Provides debounced search across multiple fields with async results loading.
 *
 * Note: Uses Stimulus 1.x Data Map API (this.data.get) instead of Values API
 * since the project uses stimulus ^1.1.0
 */
export default class extends ApplicationController {
    static targets = [
        "input",
        "clearButton",
        "loadingIndicator",
        "resultsContainer",
        "cardPane",
        "tableBody",
        "emptyState",
        "originalContent"
    ];

    connect() {
        this.debounceTimer = null;
        this.currentQuery = "";
        this.isSearchActive = false;
        this.originalCardContent = null;
        this.originalTableContent = null;

        // Store original content for restoration when search is cleared
        this._storeOriginalContent();
    }

    /**
     * Get URL from data attribute (Stimulus 1.x API)
     */
    get url() {
        return this.data.get("url");
    }

    /**
     * Get debounce milliseconds from data attribute (default: 300)
     */
    get debounceMs() {
        var value = this.data.get("debounceMs");
        return value ? parseInt(value, 10) : 300;
    }

    /**
     * Get minimum characters before searching (default: 1)
     */
    get minChars() {
        var value = this.data.get("minChars");
        return value ? parseInt(value, 10) : 1;
    }

    disconnect() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    }

    /**
     * Handle input event with debouncing
     * @param {Event} event - The input event
     */
    search(event) {
        var self = this;
        var query = event.target.value.trim();

        // Show/hide clear button based on input
        this._updateClearButtonVisibility(query);

        // Clear previous timer
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        // If query is empty or too short, restore original content
        if (query.length < this.minChars) {
            this._restoreOriginalContent();
            return;
        }

        // Show loading indicator
        this._showLoading();

        // Debounce the search request
        this.debounceTimer = setTimeout(function() {
            self._performSearch(query);
        }, this.debounceMs);
    }

    /**
     * Clear the search input and restore original content
     */
    clear() {
        if (this.hasInputTarget) {
            this.inputTarget.value = "";
            this._updateClearButtonVisibility("");
        }
        this._restoreOriginalContent();
    }

    /**
     * Store original content for later restoration
     * @private
     */
    _storeOriginalContent() {
        // Store card pane content
        if (this.hasCardPaneTarget) {
            this.originalCardContent = this.cardPaneTarget.innerHTML;
        }

        // Store table body content
        if (this.hasTableBodyTarget) {
            this.originalTableContent = this.tableBodyTarget.innerHTML;
        }
    }

    /**
     * Perform the search request
     * @param {string} query - The search query
     * @private
     */
    _performSearch(query) {
        if (query === this.currentQuery && this.isSearchActive) {
            return;
        }

        this.currentQuery = query;
        this.isSearchActive = true;

        var self = this;
        var url = this.url + "?q=" + encodeURIComponent(query);

        this.railsGet({ url: url, dataType: 'application/json' })
            .then(function(response) {
                // Only update if this is still the current query (prevent race conditions)
                if (query === self.currentQuery) {
                    self._updateResults(response);
                }
            })
            .catch(function(error) {
                console.error("Live search error:", error);
                self._showError();
            })
            .then(function() {
                // finally equivalent
                self._hideLoading();
            });
    }

    /**
     * Update the UI with search results
     * @param {Object} response - The search response
     * @private
     */
    _updateResults(response) {
        if (!response) {
            this._showEmptyState();
            return;
        }

        var hasResults = response.subscriptions && response.subscriptions.length > 0;

        if (!hasResults) {
            this._showEmptyState();
            return;
        }

        this._hideEmptyState();

        // Update card view
        if (this.hasCardPaneTarget && response.body) {
            // Wrap results in the list structure
            this.cardPaneTarget.innerHTML = '<ul class="list">' + response.body + '</ul>';
        }

        // Update table view
        if (this.hasTableBodyTarget && response.table_rows) {
            this.tableBodyTarget.innerHTML = response.table_rows;
        }

        // Dispatch event for other controllers to react (e.g., update results count)
        this.element.dispatchEvent(new CustomEvent("livesearch:results", {
            detail: {
                subscriptions: response.subscriptions,
                matches: response.matches,
                canLoadMore: response.can_load_more,
                query: this.currentQuery
            },
            bubbles: true
        }));
    }

    /**
     * Restore original content when search is cleared
     * @private
     */
    _restoreOriginalContent() {
        this.isSearchActive = false;
        this.currentQuery = "";

        this._hideLoading();
        this._hideEmptyState();

        // Restore card pane content
        if (this.hasCardPaneTarget && this.originalCardContent) {
            this.cardPaneTarget.innerHTML = this.originalCardContent;
        }

        // Restore table body content
        if (this.hasTableBodyTarget && this.originalTableContent) {
            this.tableBodyTarget.innerHTML = this.originalTableContent;
        }

        // Dispatch event to notify search was cleared
        this.element.dispatchEvent(new CustomEvent("livesearch:cleared", {
            bubbles: true
        }));
    }

    /**
     * Show loading indicator
     * @private
     */
    _showLoading() {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.classList.remove("hidden");
        }
    }

    /**
     * Hide loading indicator
     * @private
     */
    _hideLoading() {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.classList.add("hidden");
        }
    }

    /**
     * Show empty state message
     * @private
     */
    _showEmptyState() {
        var escapedQuery = this._escapeHtml(this.currentQuery);

        // Clear existing content
        if (this.hasCardPaneTarget) {
            this.cardPaneTarget.innerHTML =
                '<ul class="list">' +
                    '<div class="subscriptions-live-search__no-results">' +
                        '<i class="material-icons">search_off</i>' +
                        '<span>No subscriptions found matching "<strong>' + escapedQuery + '</strong>"</span>' +
                    '</div>' +
                '</ul>';
        }

        if (this.hasTableBodyTarget) {
            this.tableBodyTarget.innerHTML =
                '<tr class="subscriptions-table__row">' +
                    '<td colspan="6" class="subscriptions-table__empty">' +
                        'No subscriptions found matching "' + escapedQuery + '"' +
                    '</td>' +
                '</tr>';
        }
    }

    /**
     * Hide empty state (if showing)
     * @private
     */
    _hideEmptyState() {
        // Empty state is replaced by results, no explicit hide needed
    }

    /**
     * Show error message
     * @private
     */
    _showError() {
        if (this.hasCardPaneTarget) {
            this.cardPaneTarget.innerHTML =
                '<ul class="list">' +
                    '<div class="subscriptions-live-search__error">' +
                        '<i class="material-icons">error_outline</i>' +
                        '<span>An error occurred while searching. Please try again.</span>' +
                    '</div>' +
                '</ul>';
        }
    }

    /**
     * Update clear button visibility
     * @param {string} query - Current query value
     * @private
     */
    _updateClearButtonVisibility(query) {
        if (this.hasClearButtonTarget) {
            if (query.length > 0) {
                this.clearButtonTarget.classList.remove("hidden");
            } else {
                this.clearButtonTarget.classList.add("hidden");
            }
        }
    }

    /**
     * Escape HTML to prevent XSS
     * @param {string} text - Text to escape
     * @returns {string} Escaped text
     * @private
     */
    _escapeHtml(text) {
        var div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
    }
}
